<script setup>
import {computed, onMounted, ref} from 'vue'
import Card from "primevue/card";
import Button from 'primevue/button';
import DefaultLayout from '../../layouts/default.vue'
import WorksTable from "@/components/work/WorksTable.vue";
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import ActiveArchiveToggle from '@/components/common/ActiveArchiveToggle.vue'
import workApi from '@/api/workApi.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { useRoute } from 'vue-router';

const search = ref('');
const checked = ref(true); // true = ACTIVE, false = ARCHIVED
const status = computed(() => (checked.value ? 'ACTIVE' : 'ARCHIVED'));

const customerFilter = ref(undefined);
const activeCount = ref(null);
const archiveCount = ref(null);
const { hasPermission } = usePermissions();
const route = useRoute();
const responsible = computed(() => route.query.responsible ?? undefined);

// Подсчёт активных/архивных в текущей выборке по q
async function fetchWorkCounts({ q }) {
  const [activeResp, archiveResp] = await Promise.all([
    workApi.getWorks({ page: 1, q, status: 'ACTIVE', responsible: responsible.value }),
    workApi.getWorks({ page: 1, q, status: 'ARCHIVED', responsible: responsible.value }),
  ]);
  return {
    active: Number(activeResp?.count ?? 0),
    archive: Number(archiveResp?.count ?? 0),
  };
}

// Загрузка только архивного счетчика
async function fetchArchiveCountOnly({ q }) {
  const archiveResp = await workApi.getWorks({ page: 1, q, status: 'ARCHIVED', responsible: responsible.value });
  return Number(archiveResp?.count ?? 0);
}

onMounted(() => {
  try {
    const raw = sessionStorage.getItem('works:list:preset');
    if (raw) {
      const preset = JSON.parse(raw);
      if (preset && typeof preset === 'object') {
        // статус
        if (preset.status === 'ACTIVE' || preset.status === 'ARCHIVED') {
          checked.value = preset.status === 'ACTIVE';
        }
        // фильтр по клиенту
        if (preset.customer != null && String(preset.customer).trim().length) {
          customerFilter.value = String(preset.customer);
        }
      }
    }
  } catch (_) {
    // ignore
  } finally {
    sessionStorage.removeItem('works:list:preset');
  }
});

function handleInitialCountLoaded({ count, status: loadedStatus }) {
  if (loadedStatus === 'ACTIVE') {
    activeCount.value = count;
  } else if (loadedStatus === 'ARCHIVED') {
    archiveCount.value = count;
  }
}

</script>

<template>
  <DefaultLayout>
    <Card class="w-full flex-1 flex flex-col min-h-0"
          :pt="{
            body: { class: 'flex-1 flex flex-col h-full' },
            content: { class: 'p-0 flex-1 flex flex-col min-h-0' }
          }"
    >

    <template #title>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
          <IconField>
             <InputIcon class="pi pi-search" />
              <InputText v-model="search" placeholder="Поиск работы..."/>
           </IconField>
             <ActiveArchiveToggle
                 v-if="hasPermission('work.archive_work')"
                 v-model="checked"
                 :search="search"
                 :initial-active-count="activeCount"
                 :initial-archive-count="archiveCount"
                 :skip-initial-load="true"
                 :fetchCounts="fetchWorkCounts"
                 :fetchArchiveCountOnly="fetchArchiveCountOnly"
                 onIcon="pi pi-sun"
                 offIcon="pi pi-moon"
                 widthClass="w-36"
                 ariaLabel="Фильтр по активности работ"
             />
        </div>
             <Button
                 v-if="hasPermission('work.add_work')"
                 label="Добавить работу"
                 icon="pi pi-plus"
                 severity="info"
                 rounded
                 disabled
             />
           </div>


        </template>
        <template #content>
          <WorksTable
              :search="search"
              :status="status"
              :customer="customerFilter"
              :responsible="responsible"
              @initial-count-loaded="handleInitialCountLoaded"
          />
        </template>
      </Card>
  </DefaultLayout>
</template>

<style scoped>
</style>
